import {
  updateResults,
  editEventAttempts,
  createTrackResultsBatch,
  createFieldResultsBatch,
  createHighJumpResultsBatch,
  editHighJumpResults,
} from 'api/resultApi';
import { convertRecordToMilliseconds } from 'utils/time';
import { getResultHighJumpHeights } from 'config/admin/result';
import { ATTEMPT_MAX_COUNT } from 'config/admin/event';

/**
 * The time a track result is saved with. A DNF with nothing in the time box is
 * saved with a time of 0 (Doug, 2026-10-23: "We just need to record 0 for DNF if
 * the box is empty"), so the box can be left empty. A DNF with a time is still
 * refused (validateTrackEventResult), because DNF may have been chosen by mistake.
 */
export const trackTimeToSave = (resultValue, status) =>
  status === 'DNF' && (!resultValue || resultValue.trim() === '') ? '0.00' : resultValue;

/********************
 * Save track event results
 */
export const saveTrackEventResults = async (
  clubID,
  meetID,
  eventID,
  eventType,
  tableData,
  originalTableData
) => {
  if (tableData.length === 0) {
    throw new Error('no results');
  }

  const newResults = tableData.filter((td) => td.isNew);
  const oldResults = tableData.filter((td) => !td.isNew);

  if (newResults.length > 0) {
    // Validate all new results first
    const newResultsValidation = validateAllTrackEventResults(newResults);

    if (!newResultsValidation.isValid) {
      // ✅ FIXED: Removed \n characters, use clean formatting
      const errorMessage =
        newResultsValidation.errors.length === 1
          ? newResultsValidation.errors[0]
          : `Please fix ${newResultsValidation.errors.length} validation errors: ${newResultsValidation.errors.join('; ')}`;
      throw new Error(errorMessage);
    }

    // Handle new results - UPDATED TO USE BATCH API
    // Validate all new results first
    for (let i = 0; i < newResults.length; i++) {
      const result = newResults[i];
      // A relay team needs a house or a typed name (utils/relayTeams.js).
      if (result.isTeam && !(result.athleteName || '').trim()) {
        throw new Error('Please choose a team for the new result.');
      }
      result.resultValue = trackTimeToSave(result.resultValue, result.status);
      if (!result.resultValue) {
        throw new Error('Please input the result value for the new result.');
      }
      if (!result.resultValue.includes('.')) {
        // super simple way to automatically add a decimal so you can just enter a number like 12 instead of 12.00
        result.resultValue = `${result.resultValue}.00`;
      }
      if (!result.laneNumber) {
        throw new Error('Please input the lane for the new result.');
      }
      // Convert laneNumber to integer
      result.laneNumber = parseInt(result.laneNumber, 10);
      if (!result.status) {
        throw new Error('Please select a status for the new result.');
      }

      // Final validation after processing
      const finalValidation = validateTrackEventResult(
        result.resultValue,
        result.status
      );
      if (!finalValidation.isValid) {
        throw new Error(`${result.athleteName}: ${finalValidation.error}`);
      }
    }
    // Create all new results in a single batch call
    //console.log('Creating batch of new results:', newResults);
    await createTrackResultsBatch(
      clubID,
      meetID,
      eventID,
      eventType,
      newResults
    );
  }

  if (oldResults.length > 0) {
    const changedResults = getChangedTrackEventResults(
      tableData,
      originalTableData
    );
    if (changedResults.length === 0) {
      // No existing result was changed. That means nothing was saved only
      // when no new result was created above; a new result is a change.
      return newResults.length === 0 ? 'no-changes' : true;
    }

    // Validate changed results before sending
    const changedResultsValidation =
      validateAllTrackEventResults(changedResults);

    if (!changedResultsValidation.isValid) {
      // ✅ FIXED: Removed \n characters, use clean formatting
      const errorMessage =
        changedResultsValidation.errors.length === 1
          ? changedResultsValidation.errors[0]
          : `Please fix ${changedResultsValidation.errors.length} validation errors: ${changedResultsValidation.errors.join('; ')}`;
      throw new Error(errorMessage);
    }

    const requestBody = changedResults.map((result) => {
      const resultValue = trackTimeToSave(result.resultValue, result.status);
      return {
        resultID: result.resultID,
        clubID: clubID,
        resultRawValue: convertRecordToMilliseconds(
          resultValue.includes('.') ? resultValue : `${resultValue}.00`
        ),
        unit: 'ms',
        status: result.status,
        laneNumber: parseInt(result.laneNumber, 10), // Convert to integer
      };
    });
    await updateResults(eventID, requestBody);
  }
};

//
// TRACK EVENT VALIDATION FUNCTIONS
//
/**
 * Validates a single track event result
 * @param {string} resultValue - The time value (e.g., "12.34", "1:23.45")
 * @param {string} status - The status: 'OK', 'DNF', 'DQ', etc.
 * @returns {Object} { isValid: boolean, error: string, shouldAutoCorrect: boolean, correctedResultValue?: string, correctedStatus?: string }
 */
export const validateTrackEventResult = (resultValue, status) => {
  // Handle empty or null values
  if (!resultValue || resultValue.trim() === '') {
    if (status === 'OK') {
      return {
        isValid: false,
        error:
          'Cannot have OK status with no time result. Either enter a time or change status to DNF/DQ.',
        shouldAutoCorrect: false,
      };
    }
    // Empty value with DNF/DQ is valid
    return {
      isValid: true,
      error: null,
      shouldAutoCorrect: false,
      correctedResultValue: resultValue,
      correctedStatus: status,
    };
  }

  // Try to convert to milliseconds, but handle format errors gracefully
  let msValue = 0;
  try {
    // Normalize the format before conversion to handle partial edits
    let normalizedValue = resultValue.trim();

    // Handle cases where user is in the middle of editing (like "0.", "0.0", etc.)
    if (
      normalizedValue === '0' ||
      normalizedValue === '0.' ||
      normalizedValue === '0.0' ||
      normalizedValue === '0.00'
    ) {
      msValue = 0;
    } else if (!normalizedValue.includes('.')) {
      // If no decimal, add .00 for conversion
      normalizedValue = `${normalizedValue}.00`;
      msValue = convertRecordToMilliseconds(normalizedValue);
    } else {
      msValue = convertRecordToMilliseconds(normalizedValue);
    }
  } catch (error) {
    // If conversion fails, treat as invalid format but don't crash
    // This allows user to continue editing invalid intermediate states
    console.warn(
      'Time format conversion error:',
      error.message,
      'for value:',
      resultValue
    );

    // For intermediate editing states, just return valid to avoid crashes
    // The final validation will catch any real format issues
    return {
      isValid: true,
      error: null,
      shouldAutoCorrect: false,
      correctedResultValue: resultValue,
      correctedStatus: status,
    };
  }

  // Validation rules - these are INVALID configurations that should be prevented
  if (status === 'OK' && msValue <= 0) {
    return {
      isValid: false,
      error:
        'Cannot have OK status with no time result. Either enter a time or change status to DNF/DQ.',
      shouldAutoCorrect: false,
    };
  }

  if ((status === 'DNF' || status === 'DQ') && msValue > 0) {
    return {
      isValid: false,
      error: `Cannot have ${status} status with a time result. Either clear the time or change status to OK.`,
      shouldAutoCorrect: false, // Don't auto-correct this - let user decide
    };
  }

  // Safe auto-corrections (only when we're not losing data)
  let correctedResultValue = resultValue;
  let correctedStatus = status;
  let shouldAutoCorrect = false;

  // Safe Case 1: If user enters a time > 0 and status is not OK, auto-set status to OK
  // This is safe because we're not losing the time data
  if (msValue > 0 && status !== 'OK') {
    correctedStatus = 'OK';
    shouldAutoCorrect = true;
  }

  // Safe Case 2: If user sets status to OK but time is empty, auto-set status to DNF
  // This is safe because there's no time data to lose
  if (status === 'OK' && msValue <= 0) {
    correctedStatus = 'DNF';
    shouldAutoCorrect = true;
  }

  return {
    isValid: true,
    error: null,
    shouldAutoCorrect,
    correctedResultValue,
    correctedStatus,
  };
};

/**
 * Validates all track event results before saving
 * @param {Array} results - Array of track results to validate
 * @returns {Object} { isValid: boolean, errors: Array }
 */
export const validateAllTrackEventResults = (results) => {
  const errors = [];

  results.forEach((result, index) => {
    const athleteName =
      result.athleteName ||
      `${result.athleteFirstName} ${result.athleteLastName}`;

    // First check for basic format issues
    if (result.resultValue && result.resultValue.trim() !== '') {
      try {
        const normalizedValue = result.resultValue.includes('.')
          ? result.resultValue
          : `${result.resultValue}.00`;
        convertRecordToMilliseconds(normalizedValue);
      } catch (formatError) {
        errors.push(
          `${athleteName}: Invalid time format "${result.resultValue}". Please use a valid time format (e.g., 12.34, 1:23.45).`
        );
        return; // Skip further validation if format is invalid
      }
    }

    const validation = validateTrackEventResult(
      result.resultValue,
      result.status
    );

    if (!validation.isValid) {
      errors.push(`${athleteName}: ${validation.error}`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors: errors,
  };
};

/**
 * Helper function to get changed track event results for comparison
 * @param {Array} tableData - Current table data
 * @param {Array} originalTableData - Original data from API
 * @returns {Array} Array of changed results
 */
const getChangedTrackEventResults = (tableData, originalTableData) => {
  return tableData.filter((row) => {
    if (row.isNew) return false;
    const original = originalTableData.find(
      (orig) => orig.resultID === row.resultID
    );
    if (!original) return false;

    // Compare laneNumber, resultValue, and status
    return (
      row.laneNumber !== original.laneNumber ||
      row.resultValue !== original.resultValue ||
      row.status !== original.status
    );
  });
};

/********************
 * Save field event results
 */
export const saveFieldEventResults = async (
  clubID,
  meetID,
  eventID,
  tableData,
  originalTableData
) => {
  if (tableData.length === 0) {
    throw new Error('no results');
  }
  const newResults = tableData.filter((td) => td.isNew);
  const oldResults = tableData.filter((td) => !td.isNew);
  const hasNewAttempt =
    tableData[0][`attempt${tableData[0].attempts.length}`]?.isNew;

  const editAttempts = getChangedFieldEventResultAttempts(
    oldResults,
    originalTableData
  );

  // if ((newResults.length > 0 || editAttempts.length > 0) && hasNewAttempt) {
  //   throw new Error(
  //     'Please save any unsaved edits or add the new athlete before creating a new attempt.'
  //   );
  // }

  if (newResults.length > 0) {
    // Add the new athlete with validation
    validationAthleteAdd(newResults, originalTableData);

    // Validate all attempts for new athletes
    const validationErrors = [];
    newResults.forEach((result) => {
      const athleteName =
        result.athleteName ||
        `${result.athleteFirstName} ${result.athleteLastName}`;

      for (let i = 1; i <= ATTEMPT_MAX_COUNT; i++) {
        const attempt = result[`attempt${i}`];
        if (attempt) {
          // Validate each attempt
          const validation = validateFieldEventAttempt(
            attempt.distance,
            attempt.status
          );
          if (!validation.isValid) {
            validationErrors.push(
              `${athleteName} - Attempt ${i}: ${validation.error}`
            );
          }
        }
      }
    });

    // If there are validation errors, throw them all at once
    if (validationErrors.length > 0) {
      const errorMessage =
        'Please fix the following validation errors:\n' +
        validationErrors.join('\n');
      throw new Error(errorMessage);
    }

    let resultDtos = [];
    newResults.forEach((result) => {
      for (let i = 1; i <= ATTEMPT_MAX_COUNT; i++) {
        const attempt = result[`attempt${i}`];
        if (attempt) {
          // Use validation function to get correct resultType and status
          const { resultType, status, distance } =
            getFieldEventResultTypeAndStatus(attempt.distance, attempt.status);

          resultDtos.push({
            athleteID: result.athleteID || '',
            athleteFirstName: result.athleteFirstName || '',
            athleteLastName: result.athleteLastName || '',
            athleteNumber: result.athleteNumber || 0,
            athleteAge: result.athleteAge || 0,
            athleteGender: result.athleteGender || '',
            resultType: resultType, // ✅ Now validated
            attemptNumber: i,
            distance: distance, // ✅ Now validated/corrected
            status: status, // ✅ Now validated/corrected
            unit: 'm',
            timestamp: new Date().toISOString(),
          });
        }
      }
    });

    //console.log('Would have added new athletes with result DTOs:', resultDtos);
    await createFieldResultsBatch(clubID, meetID, eventID, resultDtos);
  }

  if (hasNewAttempt) {
    // Validate new attempts for existing athletes
    const validationErrors = [];
    tableData.forEach((result) => {
      if (result.isNew) return; // Skip new athletes

      const athleteName =
        result.athleteName ||
        `${result.athleteFirstName} ${result.athleteLastName}`;

      for (let i = 1; i <= ATTEMPT_MAX_COUNT; i++) {
        const attempt = result[`attempt${i}`];
        if (attempt?.isNew) {
          const validation = validateFieldEventAttempt(
            attempt.distance,
            attempt.status
          );
          if (!validation.isValid) {
            validationErrors.push(
              `${athleteName} - Attempt ${i}: ${validation.error}`
            );
          }
        }
      }
    });

    // If there are validation errors, throw them all at once
    if (validationErrors.length > 0) {
      const errorMessage =
        'Please fix the following validation errors:\n' +
        validationErrors.join('\n');
      throw new Error(errorMessage);
    }

    // create the new attempts
    let resultDtos = [];
    tableData.forEach((result) => {
      // Skip new athletes - they're handled above
      if (result.isNew) return;

      // For existing athletes, use their data directly from the API response
      for (let i = 1; i <= ATTEMPT_MAX_COUNT; i++) {
        const attempt = result[`attempt${i}`];
        if (attempt?.isNew) {
          const { resultType, status, distance } =
            getFieldEventResultTypeAndStatus(attempt.distance, attempt.status);
          resultDtos.push({
            athleteID: result.athleteID,
            athleteFirstName: result.athleteFirstName,
            athleteLastName: result.athleteLastName,
            athleteNumber: result.athleteNumber,
            athleteAge: result.athleteAge,
            athleteGender: result.athleteGender,
            resultType: resultType,
            attemptNumber: i,
            distance: distance,
            status: status,
            unit: 'm',
            timestamp: new Date().toISOString(),
          });
        }
      }
    });

    //console.log('Would have added new attempts for existing athletes with result DTOs:', resultDtos);
    // Nothing to send when the only new attempt belongs to a new athlete,
    // whose attempts were saved above. That is the case for the first athlete
    // of an event with no results: their starter attempt is new, so this
    // block runs, but there is no existing athlete to add an attempt for.
    // Sending the empty list made the server refuse the save ("You must
    // include at least one result") after the athlete had already been saved.
    if (resultDtos.length > 0) {
      await createFieldResultsBatch(clubID, meetID, eventID, resultDtos);
    }
  }

  if (editAttempts.length > 0) {
    // Validate edited attempts before sending
    const validationErrors = [];
    editAttempts.forEach((attempt, index) => {
      const validation = validateFieldEventAttempt(
        attempt.distance,
        attempt.status
      );
      if (!validation.isValid) {
        validationErrors.push(`Edit attempt ${index + 1}: ${validation.error}`);
      }
    });

    // If there are validation errors, throw them all at once
    if (validationErrors.length > 0) {
      const errorMessage =
        'Please fix the following validation errors:\n' +
        validationErrors.join('\n');
      throw new Error(errorMessage);
    }

    // console.log('Would have edited existing attempts with:', {
    //   eventID,
    //   attempts: editAttempts,
    // });

    await editEventAttempts({
      eventID,
      attempts: editAttempts,
    });
  }

  // Nothing was sent: no new athlete, no new attempt and no edited attempt.
  if (newResults.length === 0 && !hasNewAttempt && editAttempts.length === 0) {
    return 'no-changes';
  }

  return true;
};

/**
 * Validates a single field event attempt
 * @param {number} distance - The distance value
 * @param {string} status - The status: 'OK', 'Foul', or 'NR'
 * @returns {Object} { isValid: boolean, error: string, correctedDistance?: number, correctedStatus?: string }
 */
export const validateFieldEventAttempt = (distance, status) => {
  const numDistance = Number(distance);

  // Validation rules
  if (status === 'OK' && numDistance <= 0) {
    return {
      isValid: false,
      error:
        'Cannot have OK status with distance 0. Either enter a distance or change status to Foul/NR.',
    };
  }

  if ((status === 'Foul' || status === 'NR') && numDistance > 0) {
    return {
      isValid: false,
      error: `Cannot have ${status} status with distance > 0. Either set distance to 0 or change status to OK.`,
    };
  }

  // Auto-corrections
  let correctedDistance = numDistance;
  let correctedStatus = status;

  // If user enters distance > 0, auto-set status to OK
  if (numDistance > 0 && status !== 'OK') {
    correctedStatus = 'OK';
  }

  // If user sets status to Foul/NR, auto-set distance to 0
  if ((status === 'Foul' || status === 'NR') && numDistance > 0) {
    correctedDistance = 0;
  }

  return {
    isValid: true,
    error: null,
    correctedDistance,
    correctedStatus,
  };
};

const getFieldEventResultTypeAndStatus = (distance, status) => {
  // Validate first
  const validation = validateFieldEventAttempt(distance, status);
  if (!validation.isValid) {
    throw new Error(validation.error);
  }

  // Use corrected values
  const correctedDistance = validation.correctedDistance;
  const correctedStatus = validation.correctedStatus;

  if (correctedStatus === 'OK') {
    return { resultType: 'Valid', status: 'OK', distance: correctedDistance }; // ← Use correctedDistance
  } else if (correctedStatus === 'Foul') {
    return { resultType: 'Foul', status: 'Foul', distance: correctedDistance }; // ← Use correctedDistance
  } else if (correctedStatus === 'NR') {
    return { resultType: 'NR', status: 'NR', distance: correctedDistance }; // ← Use correctedDistance
  }

  throw new Error(`Invalid status: ${correctedStatus}`);
};

/********************
 * Save high jump results
 */

export const saveHighJumpEventResults = async (
  clubID,
  meetID,
  eventID,
  tableData,
  originalTableData
) => {
  if (tableData.length === 0) {
    throw new Error('no results');
  }

  // Check elimination rules for ALL data
  const eliminationValidation = validateHighJumpEliminationRules(tableData);
  if (!eliminationValidation.isValid) {
    const errorMessage =
      eliminationValidation.errors.length === 1
        ? eliminationValidation.errors[0]
        : `Please fix ${eliminationValidation.errors.length} elimination rule violations:${eliminationValidation.errors.join(' ')}`;
    throw new Error(errorMessage);
  }

  const newResults = tableData.filter((td) => td.isNew);
  const editAttempts = getChangedHighJumpEventResultAttempts(
    tableData,
    originalTableData
  );

  console.log('High Jump editAttempts:', editAttempts);

  const hasNewAttempt =
    tableData[0]?.heightAttempts &&
    tableData[0].heightAttempts.length > 0 &&
    tableData[0].heightAttempts[tableData[0].heightAttempts.length - 1]?.isNew;

  // Validate new results before saving
  if (newResults.length > 0) {
    const validation = validateAllHighJumpResults(newResults);

    if (!validation.isValid) {
      const errorMessage =
        'Please fix the following validation errors:\n' +
        validation.errors.join('\n');
      throw new Error(errorMessage);
    }

    // add new athlete with validated data
    validationAthleteAdd(newResults, originalTableData);
    let resultDtos = [];

    // Use the validated results which now have correct status and resultType
    validation.validatedResults.forEach((result) => {
      for (let i = 0; i < result.heightAttempts.length; i++) {
        const attempt = result.heightAttempts[i];

        // Don't filter out any attempts - even NR results need to be saved
        // Only skip if the attempt object is completely missing or invalid
        if (
          !attempt ||
          attempt.height === undefined ||
          attempt.height === null
        ) {
          continue;
        }

        resultDtos.push({
          athleteID: result.athleteID || '',
          athleteFirstName: result.athleteFirstName || '',
          athleteLastName: result.athleteLastName || '',
          athleteNumber: result.athleteNumber || 0,
          athleteAge: result.athleteAge || 0,
          athleteGender: result.athleteGender || '',
          attemptNumber: 0, // attempt number is not used for high jump
          distance: attempt.height,
          resultRawValue: '',
          resultValue: `${attempt.attempt1 || '.'},${attempt.attempt2 || '.'},${attempt.attempt3 || '.'}`,
          unit: 'm',
          status: attempt.status, // Use validated status
          resultType: attempt.resultType, // Use validated resultType
          timestamp: new Date().toISOString(),
        });
      }
    });

    console.log('High Jump resultDtos for new athlete:', resultDtos);

    if (resultDtos.length > 0) {
      await createHighJumpResultsBatch(clubID, meetID, eventID, resultDtos);
    } else {
      console.warn('No valid height attempts found for new athlete');
    }
  }

  if (hasNewAttempt) {
    // create the new attempts
    let resultDtos = [];
    const heighArrays = getResultHighJumpHeights(tableData);

    // Validate new height attempts for all athletes
    const newHeightAttempts = [];
    tableData.forEach((result) => {
      const newAttempts =
        result.heightAttempts?.filter((attempt) => attempt.isNew) || [];
      if (newAttempts.length > 0) {
        newHeightAttempts.push({
          ...result,
          heightAttempts: newAttempts,
        });
      }
    });

    if (newHeightAttempts.length > 0) {
      const validation = validateAllHighJumpResults(newHeightAttempts);

      if (!validation.isValid) {
        const errorMessage =
          'Please fix the following validation errors for new heights:\n' +
          validation.errors.join('\n');
        throw new Error(errorMessage);
      }
    }

    tableData.forEach((result) => {
      for (let i = 1; i <= heighArrays.length; i++) {
        const attempt = result.heightAttempts[i - 1];
        if (attempt?.isNew) {
          // Validate this specific attempt (no status/resultType needed for new attempts)
          const attemptValidation = validateHighJumpHeightResult(
            attempt.attempt1,
            attempt.attempt2,
            attempt.attempt3,
            true // Include status/resultType for new attempts
          );

          if (!attemptValidation.isValid) {
            throw new Error(
              `Invalid attempt for ${result.athleteName || `${result.athleteFirstName} ${result.athleteLastName}`} at height ${attempt.height}m: ${attemptValidation.error}`
            );
          }

          // These are athletes who already have results, so each row came
          // from the server, where a high jump athlete's details are named
          // firstName, lastName, age and gender (athleteId for the id). The
          // athlete* names exist only on a row added in this dialog. Reading
          // just the athlete* names sent a blank name, gender and age, and
          // the server, which groups a high jump athlete's heights by id,
          // number and name, then showed the new height as a second,
          // nameless athlete.
          resultDtos.push({
            athleteID: result.athleteId || result.athleteID || '',
            athleteFirstName: result.firstName || result.athleteFirstName || '',
            athleteLastName: result.lastName || result.athleteLastName || '',
            athleteNumber: result.athleteNumber || 0,
            athleteAge: result.age || result.athleteAge || 0,
            athleteGender: result.gender || result.athleteGender || '',
            attemptNumber: 0, // attempt number is not used for high jump
            distance: attempt.height,
            resultRawValue: '',
            resultValue: `${attempt.attempt1},${attempt.attempt2},${attempt.attempt3}`,
            unit: 'm',
            status: attemptValidation.status, // Use validated status
            resultType: attemptValidation.resultType, // Use validated resultType
            timestamp: new Date().toISOString(),
          });
        }
      }
    });

    if (resultDtos.length > 0) {
      await createHighJumpResultsBatch(clubID, meetID, eventID, resultDtos);
    }
  }

  if (editAttempts.length > 0) {
    // Validate changed attempts before sending to server
    const editValidation = validateChangedHighJumpAttempts(editAttempts);

    if (!editValidation.isValid) {
      const errorMessage =
        'Please fix the following validation errors for edited attempts:\n' +
        editValidation.errors.join('\n');
      throw new Error(errorMessage);
    }

    console.log('Calling editHighJumpResults with:', {
      eventID,
      attempts: editAttempts,
    });

    // edit high jump edits
    await editHighJumpResults({
      eventID,
      attempts: editAttempts,
    });
  }

  // Nothing was sent: no new athlete, no new height and no edited attempt.
  if (newResults.length === 0 && !hasNewAttempt && editAttempts.length === 0) {
    return 'no-changes';
  }

  return true;
};

export const getChangedFieldEventResultAttempts = (
  tableData,
  originalTableData
) => {
  // console.log('=== DEBUG getChangedFieldEventResultAttempts ===');
  // console.log('tableData length:', tableData.length);
  // console.log('originalTableData length:', originalTableData.length);

  const changedAttempts = [];

  tableData.forEach((currentResult, index) => {
    // console.log(`\n--- Processing result ${index} (${currentResult.resultID}) ---`);

    // Skip new results - they are handled separately
    if (currentResult.isNew) {
      // console.log('Skipping new result');
      return;
    }

    // Find the corresponding original result
    const originalResult = originalTableData.find(
      (orig) => orig.resultID === currentResult.resultID
    );

    if (!originalResult) {
      // console.log('No matching original result found');
      return;
    }

    // console.log('Found matching original result');

    // Check each attempt (attempt1, attempt2, etc.)
    for (let i = 1; i <= ATTEMPT_MAX_COUNT; i++) {
      const currentAttempt = currentResult[`attempt${i}`];
      const originalAttempt = originalResult[`attempt${i}`];

      // console.log(`\nChecking attempt${i}:`);
      // console.log('currentAttempt:', currentAttempt);
      // console.log('originalAttempt:', originalAttempt);

      // Skip if current attempt doesn't exist
      if (!currentAttempt) {
        // console.log('No current attempt, skipping');
        continue;
      }

      // Skip new attempts - they are handled separately
      if (currentAttempt.isNew) {
        // console.log('Current attempt is new, skipping');
        continue;
      }

      // If there's no original attempt, this shouldn't happen for existing results
      if (!originalAttempt) {
        // console.log('No original attempt found - this is unexpected');
        continue;
      }

      // Compare distance and status (convert both to numbers for distance comparison)
      const currentDistance = Number(currentAttempt.distance);
      const originalDistance = Number(originalAttempt.distance);
      const currentStatus = currentAttempt.status;
      const originalStatus = originalAttempt.status;

      // console.log('Comparing values:');
      // console.log(`Distance: ${currentDistance} vs ${originalDistance} (changed: ${currentDistance !== originalDistance})`);
      // console.log(`Status: "${currentStatus}" vs "${originalStatus}" (changed: ${currentStatus !== originalStatus})`);

      if (
        currentDistance !== originalDistance ||
        currentStatus !== originalStatus
      ) {
        changedAttempts.push({
          resultID: currentAttempt.resultID,
          distance: Number(currentAttempt.distance),
          status: currentAttempt.status,
        });
      } else {
        // console.log('No change detected');
      }
    }
  });

  // console.log('\n=== FINAL RESULT ===');
  // console.log('changedAttempts:', changedAttempts);
  // console.log('changedAttempts.length:', changedAttempts.length);

  return changedAttempts;
};

export const getChangedHighJumpEventResultAttempts = (
  tableData,
  originalTableData
) => {
  // console.log('=== DEBUG getChangedHighJumpEventResultAttempts ===');
  // console.log('tableData length:', tableData.length);
  // console.log('originalTableData length:', originalTableData.length);

  const changedAttempts = [];

  tableData.forEach((currentResult, resultIndex) => {
    // console.log(`\n--- Processing result ${resultIndex} (${currentResult.resultID || currentResult.athleteId}) ---`);

    // Skip new results - they are handled separately
    if (currentResult.isNew) {
      // console.log('Skipping new result');
      return;
    }

    // Find the corresponding original result by athleteId since that's what the API data uses
    const originalResult = originalTableData.find(
      (orig) =>
        orig.athleteId === currentResult.athleteId ||
        orig.athleteId === currentResult.athleteID ||
        orig.resultID === currentResult.resultID
    );

    if (!originalResult) {
      // console.log('No matching original result found');
      return;
    }

    //console.log('Found matching original result');

    // Get the height attempts arrays
    const currentHeightAttempts = currentResult.heightAttempts || [];
    const originalHeightAttempts = originalResult.heightAttempts || [];

    //console.log('Current heightAttempts length:', currentHeightAttempts.length);
    //console.log('Original heightAttempts length:', originalHeightAttempts.length);

    // Check each height attempt
    currentHeightAttempts.forEach((currentAttempt, attemptIndex) => {
      // console.log(`\nChecking height attempt ${attemptIndex}:`);
      // console.log('currentAttempt:', currentAttempt);

      // Skip new attempts - they are handled separately
      if (currentAttempt.isNew) {
        //console.log('Current attempt is new, skipping');
        return;
      }

      // Find the corresponding original attempt by resultId
      const originalAttempt = originalHeightAttempts.find(
        (orig) => orig.resultId === currentAttempt.resultId
      );

      if (!originalAttempt) {
        //console.log('No matching original attempt found');
        return;
      }

      // console.log('originalAttempt:', originalAttempt);

      // Compare all the attempt properties
      const currentHeight = Number(currentAttempt.height);
      const originalHeight = Number(originalAttempt.height);
      const currentAttempt1 = currentAttempt.attempt1;
      const originalAttempt1 = originalAttempt.attempt1;
      const currentAttempt2 = currentAttempt.attempt2;
      const originalAttempt2 = originalAttempt.attempt2;
      const currentAttempt3 = currentAttempt.attempt3;
      const originalAttempt3 = originalAttempt.attempt3;

      // console.log('Comparing values:');
      // console.log(`Height: ${currentHeight} vs ${originalHeight} (changed: ${currentHeight !== originalHeight})`);
      // console.log(`Attempt1: "${currentAttempt1}" vs "${originalAttempt1}" (changed: ${currentAttempt1 !== originalAttempt1})`);
      // console.log(`Attempt2: "${currentAttempt2}" vs "${originalAttempt2}" (changed: ${currentAttempt2 !== originalAttempt2})`);
      // console.log(`Attempt3: "${currentAttempt3}" vs "${originalAttempt3}" (changed: ${currentAttempt3 !== originalAttempt3})`);

      // Check if any property has changed
      if (
        currentHeight !== originalHeight ||
        currentAttempt1 !== originalAttempt1 ||
        currentAttempt2 !== originalAttempt2 ||
        currentAttempt3 !== originalAttempt3
      ) {
        // console.log('Change detected, adding to changedAttempts');

        changedAttempts.push({
          resultID: currentAttempt.resultId,
          height: currentHeight,
          attempt1: currentAttempt1,
          attempt2: currentAttempt2,
          attempt3: currentAttempt3,
        });
      } else {
        // console.log('No change detected');
      }
    });
  });

  // console.log('\n=== FINAL RESULT ===');
  // console.log('changedAttempts:', changedAttempts);
  // console.log('changedAttempts.length:', changedAttempts.length);

  return changedAttempts;
};

const validationAthleteAdd = (newResults, originalTableData) => {
  const noAthlete = newResults.some((res) => !res.athleteNumber);
  if (noAthlete) {
    throw new Error('Please select an athlete for the new result.');
  }
  const duplicatedResult = newResults.find((res) =>
    originalTableData.some((td) => td.athleteNumber === res.athleteNumber)
  );
  if (duplicatedResult) {
    throw new Error(`${duplicatedResult.athleteName} already has a result.`);
  }
  return true;
};

/////////////////////////////////////////////////////////////////////////////////////
//
// HIGH JUMP VALIDATION FUNCTIONS
//

/**
 * Validates a single high jump height result (3 attempts at one height)
 * @param {string} attempt1 - First attempt: '.', '-', 'X', or 'O'
 * @param {string} attempt2 - Second attempt: '.', '-', 'X', or 'O'
 * @param {string} attempt3 - Third attempt: '.', '-', 'X', or 'O'
 * @param {boolean} includeStatusType - Whether to calculate status and resultType (default: true)
 * @returns {Object} { isValid: boolean, error: string, status?: string, resultType?: string }
 */
export const validateHighJumpHeightResult = (
  attempt1,
  attempt2,
  attempt3,
  includeStatusType = true
) => {
  const attempts = [attempt1, attempt2, attempt3];

  // Check if all attempts use valid characters
  const validChars = ['.', '-', 'X', 'O'];
  for (let i = 0; i < attempts.length; i++) {
    if (!validChars.includes(attempts[i])) {
      return {
        isValid: false,
        error: `Invalid attempt value "${attempts[i]}". Must be one of: . - X O`,
        status: null,
        resultType: null,
      };
    }
  }

  // Find the position of first 'O' (clear)
  let firstClearIndex = attempts.indexOf('O');

  // If there's a clear, validate that all subsequent attempts are only '.' or '-'
  if (firstClearIndex !== -1) {
    for (let i = firstClearIndex + 1; i < attempts.length; i++) {
      if (attempts[i] !== '.' && attempts[i] !== '-') {
        return {
          isValid: false,
          error: `Attempt ${i + 1} "${attempts[i]}" is not allowed after a clear (O). Only "." or "-" are allowed after a successful attempt.`,
          status: null,
          resultType: null,
        };
      }
    }
  }

  // Check for multiple clears (more than one 'O')
  const clearCount = attempts.filter((attempt) => attempt === 'O').length;
  if (clearCount > 1) {
    return {
      isValid: false,
      error: `Multiple clears (O) are not allowed. Only one clear per height is possible.`,
      status: null,
      resultType: null,
    };
  }

  // Determine status and resultType based on attempt combination (only if requested)
  let status = null,
    resultType = null;

  if (includeStatusType) {
    const hasAttempted = attempts.some(
      (attempt) => attempt === 'X' || attempt === 'O'
    );
    const hasCleared = attempts.includes('O');
    const allNoAttemptOrPass = attempts.every(
      (attempt) => attempt === '.' || attempt === '-'
    );

    if (allNoAttemptOrPass) {
      // All attempts are either not attempted (.) or passed (-)
      status = 'NR';
      resultType = 'NR';
    } else if (hasCleared) {
      // Has at least one clear
      status = 'OK';
      resultType = 'Valid';
    } else if (hasAttempted) {
      // Has attempted but no clears (all X's with possibly some . or -)
      status = 'OK';
      resultType = 'Valid';
    } else {
      // Shouldn't reach here with current logic, but fallback
      status = 'NR';
      resultType = 'NR';
    }
  }

  const result = {
    isValid: true,
    error: null,
  };

  if (includeStatusType) {
    result.status = status;
    result.resultType = resultType;
  }

  return result;
};

/**
 * Validates all height attempts for a single athlete
 * @param {Array} heightAttempts - Array of height attempt objects with attempt1, attempt2, attempt3
 * @param {boolean} includeStatusType - Whether to calculate status and resultType (default: true)
 * @returns {Object} { isValid: boolean, errors: Array, validatedAttempts: Array }
 */
export const validateAthleteHighJumpAttempts = (
  heightAttempts,
  includeStatusType = true
) => {
  const errors = [];
  const validatedAttempts = [];

  heightAttempts.forEach((attempt, index) => {
    const validation = validateHighJumpHeightResult(
      attempt.attempt1,
      attempt.attempt2,
      attempt.attempt3,
      includeStatusType
    );

    if (!validation.isValid) {
      errors.push(`Height ${attempt.height}m: ${validation.error}`);
    }

    const validatedAttempt = {
      ...attempt,
      isValid: validation.isValid,
      error: validation.error,
    };

    if (includeStatusType) {
      validatedAttempt.status = validation.status;
      validatedAttempt.resultType = validation.resultType;
    }

    validatedAttempts.push(validatedAttempt);
  });

  return {
    isValid: errors.length === 0,
    errors: errors,
    validatedAttempts: validatedAttempts,
  };
};

/**
 * Validates all athletes' high jump attempts before saving
 * @param {Array} newResults - Array of new athlete results to validate
 * @param {boolean} includeStatusType - Whether to calculate status and resultType (default: true)
 * @returns {Object} { isValid: boolean, errors: Array, validatedResults: Array }
 */
export const validateAllHighJumpResults = (
  newResults,
  includeStatusType = true
) => {
  const allErrors = [];
  const validatedResults = [];

  newResults.forEach((result, resultIndex) => {
    const athleteName =
      result.athleteName ||
      `${result.athleteFirstName} ${result.athleteLastName}`;

    if (!result.heightAttempts || !Array.isArray(result.heightAttempts)) {
      allErrors.push(`${athleteName}: No height attempts found`);
      return;
    }

    const athleteValidation = validateAthleteHighJumpAttempts(
      result.heightAttempts,
      includeStatusType
    );

    if (!athleteValidation.isValid) {
      athleteValidation.errors.forEach((error) => {
        allErrors.push(`${athleteName}: ${error}`);
      });
    }

    validatedResults.push({
      ...result,
      heightAttempts: athleteValidation.validatedAttempts,
      isValid: athleteValidation.isValid,
      errors: athleteValidation.errors,
    });
  });

  return {
    isValid: allErrors.length === 0,
    errors: allErrors,
    validatedResults: validatedResults,
  };
};

/**
 * Validates all changed high jump attempts before editing (no status/resultType needed)
 * @param {Array} changedAttempts - Array of changed attempt objects with resultID, height, attempt1, attempt2, attempt3
 * @returns {Object} { isValid: boolean, errors: Array }
 */
export const validateChangedHighJumpAttempts = (changedAttempts) => {
  const errors = [];

  changedAttempts.forEach((attempt, index) => {
    const validation = validateHighJumpHeightResult(
      attempt.attempt1,
      attempt.attempt2,
      attempt.attempt3,
      false // Don't include status/resultType for edits
    );

    if (!validation.isValid) {
      errors.push(`Height ${attempt.height}m: ${validation.error}`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors: errors,
  };
};

/**
 * Validates that athletes don't have results at higher heights after being eliminated at lower heights
 * @param {Array} tableData - Current table data with height attempts
 * @returns {Object} { isValid: boolean, errors: Array }
 */
export const validateHighJumpEliminationRules = (tableData) => {
  const errors = [];

  tableData.forEach((result) => {
    const athleteName =
      result.athleteName ||
      `${result.athleteFirstName} ${result.athleteLastName}`;

    if (!result.heightAttempts || !Array.isArray(result.heightAttempts)) {
      return; // Skip if no height attempts
    }

    // Sort height attempts by height to ensure we check in order
    const sortedHeights = [...result.heightAttempts].sort(
      (a, b) => a.height - b.height
    );

    let totalFailures = 0; // Total X's accumulated across all heights
    let isEliminated = false;
    let eliminationHeight = null;

    for (let i = 0; i < sortedHeights.length; i++) {
      const attempt = sortedHeights[i];
      const { attempt1, attempt2, attempt3, height } = attempt;
      const attempts = [attempt1, attempt2, attempt3];

      // Count failures (X) and successes (O) at this height
      const failuresAtHeight = attempts.filter((a) => a === 'X').length;
      const successesAtHeight = attempts.filter((a) => a === 'O').length;

      // Add failures from this height to total
      totalFailures += failuresAtHeight;

      // If there's a success (O) at this height, reset the failure count
      if (successesAtHeight > 0) {
        totalFailures = 0; // Reset on successful clear
      }

      // Check if athlete is eliminated (3 total failures)
      if (totalFailures >= 3) {
        isEliminated = true;
        eliminationHeight = height;
        break; // Stop processing - athlete is eliminated
      }
    }

    // If eliminated, check if there are any attempts at higher heights
    if (isEliminated) {
      // Find the index where elimination occurred
      const eliminationIndex = sortedHeights.findIndex(
        (h) => h.height === eliminationHeight
      );

      // Check all heights after elimination
      for (let j = eliminationIndex + 1; j < sortedHeights.length; j++) {
        const higherAttempt = sortedHeights[j];
        const higherHasAttempts = [
          higherAttempt.attempt1,
          higherAttempt.attempt2,
          higherAttempt.attempt3,
        ].some((a) => a === 'O' || a === 'X'); // Any actual attempt (not just dots or dashes)

        if (higherHasAttempts) {
          errors.push(
            `${athleteName}: Cannot have attempts at ${higherAttempt.height}m after being eliminated ` +
              `(3 failures reached at ${eliminationHeight}m). Please clear the higher height attempts or fix the elimination.`
          );
          break; // Only report the first violation per athlete
        }
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors: errors,
  };
};
